import { MarkdownEditor, type AvailableVariable } from '@westhetics/editor'
import '@westhetics/editor/styles.css'
import {
  VARIABLE_CATALOG,
  catalogGroupLabel,
  catalogSampleValues,
  catalogVariableLabel,
  type DocumentAudience,
} from '@westhetics/legal'
import { ActionError, AttentionBlock, Button, ConfirmDialog, Skeleton, toast } from '@westhetics/ui'
import { useCallback, useMemo, useRef, useState } from 'react'
import { LoadError } from '@/app/load-error'
import { isApiError } from '@/lib/api'
import { errorMessage } from '@/lib/errors'
import { useDiscardDraft, useTemplateVersion, useUpdateTemplate, type Template } from './api'

/**
 * Aba Editor: o `MarkdownEditor` em tela larga, com as variáveis do catálogo, a pré-visualização e
 * a comparação contra a versão vigente. Salva sozinho; o rascunho é um só, com trava otimista
 * (RN-DOC-44). Nada aqui chega às clínicas até virar versão.
 */

export interface SaveState {
  savedAt: Date | null
  saving: boolean
  dirty: boolean
}

/**
 * As variáveis do catálogo para o menu, com o rótulo do público do documento: num contrato de aluno,
 * `{{patient.name}}` aparece como "Nome do aluno" no grupo "Aluno" (P-DOC-30). O caminho não muda.
 */
function variablesFor(audience: DocumentAudience): AvailableVariable[] {
  return VARIABLE_CATALOG.filter((v) => v.type !== 'block').map((v) => ({
    path: v.path,
    label: catalogVariableLabel(v, audience),
    example: v.example || undefined,
    group: catalogGroupLabel(v.group, audience),
  }))
}
const SAMPLE_VALUES = catalogSampleValues()

export function TemplateEditorTab({
  template,
  onSaveState,
}: {
  template: Template
  onSaveState: (state: SaveState) => void
}) {
  const current = template.currentVersion
  const version = useTemplateVersion(template.id, current?.number)
  // Sem rascunho, o editor abre pela versão vigente; por isso ela precisa chegar antes.
  const needsVersion = Boolean(current)
  // Descartar as alterações remonta o editor (chave nova) já com o texto da versão vigente e com a
  // revisão que o descarte devolveu.
  const [reset, setReset] = useState<{ count: number; revision: number | null }>({
    count: 0,
    revision: null,
  })

  if (needsVersion && version.isError) {
    return (
      <LoadError
        title="Não conseguimos carregar o texto da versão vigente"
        error={version.error}
        onRetry={() => void version.refetch()}
      />
    )
  }
  if (needsVersion && !version.data) {
    return (
      <div role="status" aria-label="Carregando" className="flex flex-col gap-3">
        <Skeleton className="h-[44px] w-full" />
        <Skeleton className="h-[420px] w-full" />
      </div>
    )
  }

  return (
    <Editor
      key={reset.count}
      template={template}
      publishedMarkdown={version.data?.markdown ?? null}
      startFromPublished={reset.count > 0}
      startRevision={reset.revision ?? template.draftRevision}
      onSaveState={onSaveState}
      onReset={(revision) => setReset((last) => ({ count: last.count + 1, revision }))}
    />
  )
}

function Editor({
  template,
  publishedMarkdown,
  startFromPublished,
  startRevision,
  onSaveState,
  onReset,
}: {
  template: Template
  publishedMarkdown: string | null
  /** Depois de descartar: o rascunho que ainda estiver no cache não vale mais. */
  startFromPublished: boolean
  /** A revisão com que a primeira gravação sai (`draftRevision`). */
  startRevision: number
  onSaveState: (state: SaveState) => void
  onReset: (revision: number) => void
}) {
  const update = useUpdateTemplate(template.id)
  const discard = useDiscardDraft(template.id)
  const isArchived = template.state === 'archived'
  const variables = useMemo(() => variablesFor(template.audience), [template.audience])

  // O texto com que o editor abre: o rascunho, ou a versão vigente quando não há rascunho.
  const [startDraft] = useState(() => (startFromPublished ? null : template.draft))
  const [initial] = useState(() => startDraft?.markdown ?? publishedMarkdown ?? '')
  const [conflict, setConflict] = useState<string | null>(null)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [discarding, setDiscarding] = useState(false)

  // A fila do salvamento vive fora do render: um pedido por vez, sempre com a revisão que voltou
  // do anterior; o texto mais novo espera na fila e substitui o que estava nela.
  const queue = useRef({
    revision: startRevision,
    lastSaved: initial,
    savedAt: startDraft ? new Date(startDraft.updatedAt) : null,
    next: null as string | null,
    running: false,
  })

  const { mutateAsync } = update
  const save = useCallback(
    async (markdown: string) => {
      const state = queue.current
      state.next = markdown
      if (state.running) return
      state.running = true
      try {
        while (state.next !== null) {
          const text = state.next
          state.next = null
          if (text === state.lastSaved) continue
          onSaveState({ savedAt: state.savedAt, saving: true, dirty: true })
          const saved = await mutateAsync({ draft: { markdown: text, revision: state.revision } })
          state.revision = saved.draftRevision
          state.lastSaved = text
          state.savedAt = saved.draft ? new Date(saved.draft.updatedAt) : new Date()
          setSaveError(null)
          onSaveState({ savedAt: state.savedAt, saving: false, dirty: state.next !== null })
        }
      } catch (error) {
        state.next = null
        if (isApiError(error) && error.code === 'conflict') setConflict(error.message)
        else setSaveError(errorMessage(error))
        onSaveState({ savedAt: state.savedAt, saving: false, dirty: true })
      } finally {
        state.running = false
      }
    },
    [mutateAsync, onSaveState],
  )

  const canDiscard = Boolean(template.currentVersion) && template.hasUnpublishedChanges

  return (
    <div className="flex flex-col gap-4">
      {isArchived ? (
        <AttentionBlock variant="lilac" title="Este documento está arquivado">
          Ele não aparece para as clínicas e o texto não pode ser alterado. Para voltar a editar,
          restaure-o na aba Versões.
        </AttentionBlock>
      ) : null}

      {conflict ? (
        <ActionError
          title="Este rascunho foi alterado em outro lugar"
          description={`${conflict} O que você digitou depois disso não foi salvo.`}
          actions={
            <Button variant="outline" size="sm" onClick={() => window.location.reload()}>
              Recarregar o rascunho
            </Button>
          }
        />
      ) : null}
      {saveError && !conflict ? (
        <ActionError
          title="A última alteração não foi salva"
          description={`${saveError} O texto continua na tela; a próxima alteração tenta salvar de novo.`}
          onDismiss={() => setSaveError(null)}
        />
      ) : null}

      <MarkdownEditor
        markdown={initial}
        onChange={(markdown) => void save(markdown)}
        variables={variables}
        sampleValues={SAMPLE_VALUES}
        previousVersion={publishedMarkdown ?? undefined}
        documentType={template.type}
        checkCatalog
        readOnly={isArchived || Boolean(conflict)}
        previewLabel={
          template.audience === 'patient' ? 'Como o paciente vai ler' : 'Como a pessoa vai ler'
        }
      />

      <div className="font-body text-eyebrow text-label flex flex-wrap items-center justify-between gap-3 leading-[1.6] font-light tracking-normal">
        <span>
          {template.currentVersion
            ? `As clínicas continuam usando a versão ${template.currentVersion.label} até você publicar.`
            : 'As clínicas só enxergam este documento depois da primeira versão publicada.'}
        </span>
        {canDiscard && !isArchived ? (
          <button
            type="button"
            onClick={() => setDiscarding(true)}
            className="hover:text-ink border-hairline-heavy cursor-pointer border-b pb-[2px]"
          >
            Descartar alterações
          </button>
        ) : null}
      </div>

      <ConfirmDialog
        open={discarding}
        onOpenChange={setDiscarding}
        title={`Descartar as alterações de "${template.name}"?`}
        effect={`O rascunho volta a ser igual à versão ${template.currentVersion?.label ?? ''}. O que foi escrito desde então se perde. As versões publicadas e os documentos já emitidos não mudam.`}
        confirmLabel="Descartar"
        loading={discard.isPending}
        loadingLabel="Descartando…"
        onConfirm={() =>
          discard.mutate(undefined, {
            onSuccess: (discarded) => {
              setDiscarding(false)
              toast('Alterações descartadas — o rascunho voltou à versão vigente')
              onSaveState({ savedAt: null, saving: false, dirty: false })
              onReset(discarded.draftRevision)
            },
            onError: (error) => {
              setDiscarding(false)
              toast(errorMessage(error))
            },
          })
        }
      />
    </div>
  )
}
